import {useEffect, useMemo, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Form, Input, Tabs} from 'antd';
import {toast} from 'react-toastify';
import {FormCard, spans, Hint} from 'pages/AssetManagement/v2/FormCard';
import {InstrumentActions} from 'pages/AssetManagement/v2/InstrumentActions';
import {supportedLanguages} from 'utils/constants';
import {UPDATE_STOCK_EXCHANGE} from './resolvers';
import styles from './exchanges.module.scss';

/**
 * Localised exchange content.
 *
 * Unlike instruments — where translation is a server action and you write one
 * language at a time — an exchange carries a `multiLang` array on its own
 * input, so both languages are edited together and saved in one call.
 *
 * `multiLang` is replaced wholesale by the mutation, so every language is sent
 * on every save; dropping one would delete it. The surrounding record still
 * merges, so trading config and identity are untouched by this screen.
 */
export const ExchangeContentForm = ({exchange, refetch, canUpdate}) => {
  const [form] = Form.useForm();
  const [active, setActive] = useState(supportedLanguages[0]?.value ?? 'EN');

  const [updateExchange, {loading}] = useMutation(UPDATE_STOCK_EXCHANGE, {
    errorPolicy: 'all',
  });

  // Existing content, indexed by language so a missing version starts blank
  // rather than inheriting the other language's text.
  const stored = useMemo(() => {
    const byLanguage = Object.fromEntries(
      (exchange.multiLang ?? []).map(entry => [entry.language, entry]),
    );

    // An exchange also carries resolved top-level content for the requested
    // language. Merge it in field by field rather than only when the whole
    // language row is absent: a multiLang row that fills `overview` but leaves
    // `description` null used to discard the resolved description entirely,
    // so the field rendered empty with no way to tell whether the content was
    // missing or had failed to load.
    if (exchange.language) {
      const base = byLanguage[exchange.language] ?? {
        language: exchange.language,
      };
      byLanguage[exchange.language] = {
        ...base,
        description: base.description || exchange.description || '',
        overview: base.overview || exchange.overview || '',
        whyInvest: base.whyInvest?.length
          ? base.whyInvest
          : exchange.whyInvest ?? [],
      };
    }
    return byLanguage;
  }, [exchange]);

  useEffect(() => {
    const values = {};
    supportedLanguages.forEach(language => {
      const entry = stored[language.value] ?? {};
      values[`${language.value}_description`] = entry.description ?? '';
      values[`${language.value}_overview`] = entry.overview ?? '';
      // whyInvest is [String!]; one reason per line is the least fiddly
      // editor for a short list and round-trips exactly.
      values[`${language.value}_whyInvest`] = (entry.whyInvest ?? []).join(
        '\n',
      );
    });
    form.setFieldsValue(values);
  }, [stored]);

  const handleFinish = values => {
    if (!exchange.baseCurrencyCode) {
      return toast.error(
        'This exchange has no base currency, which the update requires. Set one on Details first.',
      );
    }

    const multiLang = supportedLanguages
      .map(language => {
        const description = values[`${language.value}_description`]?.trim();
        const overview = values[`${language.value}_overview`]?.trim();
        const whyInvest = (values[`${language.value}_whyInvest`] ?? '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean);
        if (!description && !overview && !whyInvest.length) return null;
        return {
          language: language.value,
          description: description || null,
          overview: overview || null,
          whyInvest,
        };
      })
      .filter(Boolean);

    if (!multiLang.length) {
      return toast.error('Add content for at least one language');
    }

    return updateExchange({
      variables: {
        id: exchange.id,
        input: {baseCurrencyCode: exchange.baseCurrencyCode, multiLang},
      },
    })
      .then(({data}) => {
        const result = data?.updateStockExchange;
        if (result?.__typename === 'Error') return toast.error(result.message);
        toast.success('Content updated');
        refetch?.();
      })
      .catch(error => toast.error(error.message));
  };

  /**
   * How many of the three fields this language actually has.
   *
   * A single green dot meant "has at least one field", so a language with an
   * overview but no description looked identical to a complete one — which is
   * exactly the case that reads as "the description failed to load".
   */
  const completeness = language => {
    const entry = stored[language] ?? {};
    const done = [
      Boolean(entry.description),
      Boolean(entry.overview),
      Boolean((entry.whyInvest ?? []).length),
    ].filter(Boolean).length;
    return {done, total: 3};
  };

  const items = supportedLanguages.map(language => {
    const {done, total} = completeness(language.value);
    const state =
      done === total ? styles.langDotOn : done > 0 ? styles.langDotPartial : '';
    return {
      key: language.value,
      label: (
        <span
          title={
            done === total
              ? `${language.label}: all three fields written`
              : `${language.label}: ${done} of ${total} fields written`
          }>
          {language.label}
          <span className={`${styles.langDot} ${state}`} />
          {done > 0 && done < total && (
            <span className={styles.langPartialCount}>
              {done}/{total}
            </span>
          )}
        </span>
      ),
      children: (
        <>
          <div className={spans.full}>
            <Form.Item
              name={`${language.value}_description`}
              label="Description">
              <Input.TextArea
                rows={3}
                placeholder="One or two sentences describing the exchange."
              />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item name={`${language.value}_overview`} label="Overview">
              <Input.TextArea
                rows={5}
                placeholder="The longer explanation shown on the exchange page."
              />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item name={`${language.value}_whyInvest`} label="Why invest">
              <Input.TextArea rows={5} placeholder={'One reason per line'} />
            </Form.Item>
            <Hint>One reason per line. Blank lines are ignored.</Hint>
          </div>
        </>
      ),
    };
  });

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <FormCard
        title="Content"
        description="Shown to investors on the exchange page, resolved to their language — requested, then their profile language, then English">
        <div className={spans.full}>
          <Tabs activeKey={active} onChange={setActive} items={items} />
          <Hint>
            Every language is saved together. A tab left completely empty is
            removed, so clearing one deletes that version.
          </Hint>
        </div>
      </FormCard>

      <InstrumentActions
        disabled={!canUpdate || loading}
        label={loading ? 'Saving…' : 'Save content'}
      />
    </Form>
  );
};

export default ExchangeContentForm;
